import React, { createContext, useContext, useEffect, useState } from "react";
import splitFilesIntoChunks from "utilities/splitIntoChunks";
import { uploadFilesFromGoogleDrive } from "api/fileManager";
import { message } from "antd";

const FileUploadContext = createContext<any | null>(null);

export const FileUploadProvider = ({ children }: any) => {
  const [showUploader, setShowUploader] = useState(false);
  const [fileDetails, setFileDetails] = useState<any>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [totalUpload, setTotalUpload] = useState(0);
  const [filesUploaded, setFilesUploaded] = useState(0);
  const [totalFilesAmount, setTotalFilesAmount] = useState(0);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [filesWithErrors, setFilesWithErrors] = useState<any>([]);
  const [uploaderMessage, setUploaderMessage] = useState({ type: "none", message: "" });
  const [fileType, setFileType] = useState<string | null>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [isUploading, setIsUploading] = useState(false); // New state for tracking upload status
  const [hasError, setHasError] = useState(false);

  const onFileAbort = () => {
    if (abortController) {
      abortController.abort();
      setUploaderMessage({ type: "error", message: "Upload aborted" });
    }
    setFileDetails(null);
    setFileLoading(false);
    setShowProgressBar(false);
    setIsUploading(false);
  };

  const handleProgressReset = () => {
    setTotalUpload(0);
    setFilesUploaded(0);
    setTotalFilesAmount(0);
    setIsUploading(false); // Reset upload status
  };

  const handleFile = (filesUploaded: any) => {
    if (!filesUploaded && !fileDetails?.fileName) {
      setFileDetails(null);
    } else if (filesUploaded) {
      setTotalFilesAmount(filesUploaded.length);
      setFileDetails(splitFilesIntoChunks(filesUploaded, 2)); // Assuming stepsCount is 2
    }
  };

  const handleFileSubmit = async (type: any, fileUploaderApiConfig: any) => {
    setFilesUploaded(0);
    setFileLoading(true);
    setShowProgressBar(true);
    setIsUploading(true);

    const controller = new AbortController();
    setAbortController(controller);

    try {
      let filesUploadedSoFar = 0;
      for (const fileBundle of fileDetails) {
        if (!fileDetails) break;

        const processedFiles = Object.values(fileBundle);
        const fileNames = processedFiles.map((file: any) => file.name);
        const formData = new FormData();
        processedFiles.forEach((file: any) => formData.append("file", file));

        try {
          const response = type === "volume"
            ? await fileUploaderApiConfig.uploadVolumesForPointClouds(formData, { signal: controller.signal })
            : await fileUploaderApiConfig.uploadImagesForPointClouds(formData, { signal: controller.signal });

          if (response.status === 200) {
            filesUploadedSoFar += processedFiles.length;
            const newProgress = (filesUploadedSoFar / totalFilesAmount) * 100;
            setFilesUploaded(filesUploadedSoFar);
            setTotalUpload(newProgress);
          } else {
            throw new Error("Upload failed");
          }
        } catch (error: any) {
          if (error.name === "AbortError") break;
          setFilesWithErrors((prev: any) => [...prev, ...fileNames]);
        }
      }
    } catch (error) {
      setShowProgressBar(false);
      setIsUploading(false); // Reset upload status on error
    } finally {
      setFileLoading(false);
      setFileType(null);
      setAbortController(null);
      setIsUploading(false); // Reset upload status when done
    }
  };

  const handleLinkSubmit = async (link: string, type: 'volume' | 'img' | 'video' | 'e57') => {
    setShowUploader(true);
    setFileType(type);
    setFileLoading(true);
    setIsUploading(true);

    const paths = {
      'volume': 'vol_calc_files',
      'img': 'raw_drone_image',
      'video': 'raw_drone_image',
      'e57': 'raw_drone_image',
    };

    try {
      const response = await uploadFilesFromGoogleDrive(encodeURIComponent(link), paths[type]);

      if (response.status === 200) {
        setHasError(false);
        setUploaderMessage({ type: 'success', message: 'The files were uploaded' });
      } else {
        setHasError(true);
        setUploaderMessage({ type: 'error', message: 'The files from this link were not uploaded' });
      }
    } catch (error: any) {
      console.error("Upload failed with error:", error);

      setHasError(true);
      setUploaderMessage({
        type: 'error',
        message: 'The files from this link were not uploaded (server error)',
      });
    } finally {
      setFileLoading(false);
      setIsUploading(false);
      setFileType(null);
      setShowUploader(false);
    }
  };

  return (
    <FileUploadContext.Provider value={{
      fileDetails,
      fileLoading,
      totalUpload,
      filesUploaded,
      totalFilesAmount,
      showProgressBar,
      filesWithErrors,
      onFileAbort,
      handleProgressReset,
      handleFile,
      fileType,
      setFileType,
      handleFileSubmit,
      setFileDetails,
      showUploader,
      setShowUploader,
      uploaderMessage,
      setUploaderMessage,
      setFileLoading,
      setTotalUpload,
      setFilesUploaded,
      setTotalFilesAmount,
      setShowProgressBar,
      setFilesWithErrors,
      isUploading,
      setIsUploading,
      handleLinkSubmit,
      hasError,
      setHasError
    }}>
      {children}
    </FileUploadContext.Provider>
  );
};

export const useFileUpload = () => useContext(FileUploadContext);